import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { FileText, FolderOpen, Sparkles, Clock, AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { subscriptionApi, documentsApi } from '../services/api'
import ShortcutGrid from '../components/shortcuts/ShortcutGrid'
import { useShortcutNavigation } from '../hooks/useShortcutNavigation'

const DOC_TYPE_LABELS: Record<string, string> = {
  OpiniaPrawna: 'Opinia',
  Pozew: 'Pozew',
  Umowa: 'Umowa',
  PismoProcesowe: 'Pismo procesowe',
  Wezwanie: 'Wezwanie',
  Odwolanie: 'Odwołanie',
  Skarga: 'Skarga',
  Wniosek: 'Wniosek',
  Notatka: 'Notatka',
  OdpowiedzNaPozew: 'Odpowiedź na pozew',
  DodatkoweInformacje: 'Dodatkowe Informacje',
}

export default function DashboardPage() {
  const { user } = useAuth0()
  const { navigateWithShortcut } = useShortcutNavigation()

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => subscriptionApi.getCurrent().then(r => r.data),
  })

  const { data: recentDocs } = useQuery({
    queryKey: ['documents', { page: 1, pageSize: 5 }],
    queryFn: () => documentsApi.list({ page: 1 }).then(r => r.data),
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Witaj, {user?.name?.split(' ')[0] || 'użytkowniku'}!
        </h1>
        <p className="text-gray-500">Oto podsumowanie Twojej aktywności</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Strony</p>
              <p className="text-2xl font-semibold">
                {subscription?.documentsUsed || 0}/{subscription?.documentsLimit || 0}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Tokeny AI</p>
              <p className="text-2xl font-semibold">
                {((subscription?.tokensUsed || 0) / 1000).toFixed(0)}k
              </p>
            </div>
          </div>
        </div> */}

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FolderOpen className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Plan</p>
              <p className="text-2xl font-semibold capitalize">
                {subscription?.planDisplayName || subscription?.planName || 'basic'}
              </p>
            </div>
          </div>
        </div>

        {subscription?.status === 'Trial' ? (
          <a href="https://www.lextool.pl/cennik" className="card p-6 hover:shadow-md transition-shadow cursor-pointer group">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${
                subscription?.isTrialExpiringSoon ? 'bg-red-100' : 'bg-orange-100'
              }`}>
                {subscription?.isTrialExpiringSoon ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <Clock className="w-6 h-6 text-orange-600" />
                )}
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm text-gray-500">Status</p>
                <p className={`text-2xl font-semibold ${
                  subscription?.isTrialExpiringSoon ? 'text-red-600' : 'text-orange-600'
                }`}>
                  Probny
                </p>
                {subscription?.daysUntilTrialEnds !== undefined && (
                  <p className="text-xs text-gray-500">
                    {subscription.daysUntilTrialEnds <= 0
                      ? 'Konczy sie dzisiaj'
                      : subscription.daysUntilTrialEnds === 1
                        ? '1 dzien'
                        : `${subscription.daysUntilTrialEnds} dni`}
                  </p>
                )}
              </div>
              <div className="text-orange-500 group-hover:text-orange-600">
                <span className="text-xs font-medium">Wybierz plan &rarr;</span>
              </div>
            </div>
          </a>
        ) : (
          <div className="card p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100">
                <Sparkles className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Status</p>
                <p className="text-2xl font-semibold text-green-600">Aktywny</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/generate"
          className="card p-6 hover:shadow-md transition-shadow flex items-center"
        >
          <div className="p-3 bg-blue-600 rounded-lg">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold">Generuj opinię</h3>
            <p className="text-gray-500">Zadaj pytanie i otrzymaj analizę z cytatami</p>
          </div>
        </Link>

        <Link
          to="/documents"
          className="card p-6 hover:shadow-md transition-shadow flex items-center"
        >
          <div className="p-3 bg-green-600 rounded-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold">Przeglądaj dokumenty</h3>
            <p className="text-gray-500">Zobacz wszystkie swoje dokumenty</p>
          </div>
        </Link>
      </div>

      {/* Shortcut buttons */}
      <div className="card p-6">
        <ShortcutGrid onShortcutSelect={navigateWithShortcut} />
      </div>

      {/* Recent documents */}
      <div className="card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Ostatnie dokumenty</h2>
        </div>
        <div className="divide-y">
          {recentDocs?.items?.length ? (
            recentDocs.items.map((doc) => (
              <Link
                key={doc.id}
                to={`/documents/${doc.id}`}
                className="flex items-center p-4 hover:bg-gray-50"
              >
                {doc.isGenerated ? (
                  <Sparkles className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0" />
                ) : (
                  <FileText className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{doc.title}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(doc.updatedAt).toLocaleDateString('pl-PL')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {doc.isGenerated && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">AI</span>
                  )}
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {DOC_TYPE_LABELS[doc.type] ?? doc.type}
                  </span>
                </div>
              </Link>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Brak dokumentów</p>
              <Link to="/generate" className="text-blue-600 hover:underline">
                Wygeneruj pierwszą opinię
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
